/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AppError } from "@/domain/errors/common";

// --- 1. The Core Result Pattern ---

/**
 * Represents a success value.
 */
export interface Ok<T> {
  readonly ok: true;
  readonly value: T;
}

/**
 * Represents a failure value, holding a tagged error.
 */
export interface Err<E extends AppError> {
  readonly ok: false;
  readonly error: E;
}

/**
 * A container that can be either a success (Ok) or a failure (Err).
 */
export type Result<T, E extends AppError> = Ok<T> | Err<E>;

/**
 * Creates a success (Ok) result object.
 */
export function ok<T>(value: T): Ok<T> {
  return { ok: true, value };
}

/**
 * Creates a failure (Err) result object.
 */
export function err<E extends AppError>(error: E): Err<E> {
  return {
    ok: false,
    error,
  };
}

// --- 2. The Type Guards ---

/**
 * Type guard that checks if a Result is an Ok value.
 * In an `if` block, narrows the type to `Ok<T>`.
 */
export function isOk<T, E extends AppError>(
  result: Result<T, E>
): result is Ok<T> {
  return result.ok;
}

/**
 * Type guard that checks if a Result is an Err value.
 * In an `if` block, narrows the type to `Err<E>`.
 */
export function isErr<T, E extends AppError>(
  result: Result<T, E>
): result is Err<E> {
  return !result.ok;
}

// --- 3. Creating Results from Code that Throws ---

/**
 * Executes a function, capturing any thrown exceptions or promise rejections
 * into a Result object. This function's type inference is modeled on your
 * original `CaptureErr` implementation for maximum compatibility.
 *
 * @param fn The synchronous or asynchronous function to execute.
 * @param errorFactory A function that creates a specific TaggedError from the caught exception.
 */
export function safeTry<
  F extends () => any,
  R extends ReturnType<F>,
  E extends AppError,
>(
  fn: F,
  errorFactory: (cause: unknown) => E
): R extends Promise<any> ? Promise<Result<Awaited<R>, E>> : Result<R, E> {
  try {
    const result = fn();
    if (result instanceof Promise) {
      return result
        .then((value) => ok(value))
        .catch((cause) => err(errorFactory(cause))) as any; // `as any` is used here to satisfy the complex conditional type. The logic is sound.
    }

    return ok(result) as any;
  } catch (cause) {
    return err(errorFactory(cause)) as any;
  }
}

// --- 4. Utilities for Using Results ---

/**
 * Returns the success value or a provided default value if the result is an error.
 */
export function getOkOr<T, E extends AppError>(
  result: Result<T, E>,
  defaultValue: T
): T {
  return result.ok ? result.value : defaultValue;
}

/**
 * Unwraps a Result, returning the success value or throwing the error.
 * Use this at the "edge" of your application where you must exit the Result pattern.
 */
export function unwrap<T, E extends AppError>(result: Result<T, E>): T {
  if (result.ok) {
    return result.value;
  }
  throw result.error;
}

/**
 * Processes a Result by applying one of two functions, depending on the case.
 * Forces handling of both success and failure states.
 * @param result The Result to process.
 * @param onOk The function to apply if the Result is Ok.
 * @param onErr The function to apply if the Result is Err.
 * @returns The return value of whichever function is executed.
 */
export function match<T, E extends AppError, OkR, ErrR>(
  result: Result<T, E>,
  onOk: (value: T) => OkR,
  onErr: (error: E) => ErrR
): OkR | ErrR {
  if (result.ok) {
    return onOk(result.value);
  }
  return onErr(result.error);
}
